import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import OrbLoader from "../../components/common/OrbLoader";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import PageContainer from "../../components/layout/PageContainer";
import ProjectsTable, {
  ProjectDraft,
  emptyProject,
  keepLineTags,
  proposalTotal,
} from "../../components/commercial-proposals/ProjectsTable";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { useGetUsersQuery } from "../../store/apis/usersApi";
import {
  ClientAccountView,
  useGetClientAccountQuery,
  useListClientAccountsQuery,
} from "../../store/apis/clientRegistryApi";
import {
  CLIENT_ENGAGEMENTS,
  ClientEngagement,
  CommercialBusinessUnit,
  CommercialClientKind,
  CrmProspectOption,
  ProposalProjectInput,
  useCreateCommercialProposalMutation,
  useGetCommercialProposalQuery,
  useGetContentTagsQuery,
  useLazySearchCrmProspectsQuery,
} from "../../store/apis/commercialProposalApi";
import NumberField from "../../components/common/NumberField";
import { createNumberFormat } from "../../utils/numberFormat";

const toNumber = (value: string): number => {
  const parsed = Number(value.replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
};

const euro = createNumberFormat({
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

/**
 * Alta de propuesta comercial.
 *
 * El orden de los campos no es decorativo: la unidad de negocio va primero porque de ella
 * dependen tres cosas —si hay houses en los proyectos, si el selector ofrece "agencia", y
 * qué lista de clientes y prospects se carga—, así que cambiarla limpia la selección de
 * cliente en vez de dejar una incoherente.
 *
 * El número de propuesta NO se pide ni se previsualiza. Lo compone el servidor al guardar
 * y depende de la versión, que depende de lo que ya exista: adivinarlo aquí produciría un
 * número que puede no ser el que acabe emitido.
 */
const NewCommercialProposal: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const collapsed = useSelector(selectSidebarCollapsed);

  // ?from=<id> llega desde el botón "nueva versión" de la ficha. La propuesta origen
  // solo se usa para rellenar el formulario: la versión la decide el servidor al
  // guardar, contando las que ya existen de esa misma familia.
  const [searchParams] = useSearchParams();
  const sourceId = searchParams.get("from");
  const { data: sourceData, isFetching: loadingSource } =
    useGetCommercialProposalQuery(sourceId ?? "", { skip: !sourceId });
  const source = sourceData?.data ?? null;

  const [businessUnit, setBusinessUnit] =
    useState<CommercialBusinessUnit>("HOUSE_GROUP");
  const [clientKind, setClientKind] = useState<CommercialClientKind>("CLIENTE");
  const [clientEngagement, setClientEngagement] =
    useState<ClientEngagement>("ALWAYS_ON");

  const [clientAccountId, setClientAccountId] = useState("");
  const [clientBrandId, setClientBrandId] = useState("");
  const [newProspect, setNewProspect] = useState<CrmProspectOption | null>(
    null
  );

  const [name, setName] = useState("");
  const [responsableComercialUserId, setResponsableComercial] = useState("");
  const [responsablePropuestaUserId, setResponsablePropuesta] = useState("");
  const [entryDate, setEntryDate] = useState("");
  const [deliveryDeadline, setDeliveryDeadline] = useState("");
  const [estimatedActivationDate, setEstimatedActivationDate] = useState("");
  const [estimatedDurationMonths, setDurationMonths] = useState("3");
  const [projects, setProjects] = useState<ProjectDraft[]>([emptyProject()]);

  const isProspect = clientKind === "PROSPECT";
  const showHouses = businessUnit === "HOUSE_GROUP";

  // El registro es la fuente del número, así que el desplegable de cliente sale de ahí y
  // no del CRM: solo lo que tiene número puede llevar propuesta.
  //
  // Es un BUSCADOR y no un desplegable con todo dentro: House Group ya tiene 112
  // clientes, así que una sola página se quedaría corta y los últimos serían
  // invisibles sin que nada lo indicase. Además, 112 opciones no se recorren a mano.
  const [accountQuery, setAccountQuery] = useState("");
  const [debouncedAccountQuery, setDebouncedAccountQuery] = useState("");
  useEffect(() => {
    const handle = setTimeout(
      () => setDebouncedAccountQuery(accountQuery),
      250
    );
    return () => clearTimeout(handle);
  }, [accountQuery]);

  const { data: accountsData, isFetching: loadingAccounts } =
    useListClientAccountsQuery({
      businessUnit,
      type: isProspect ? "PROSPECT" : "CLIENTE",
      active: true,
      pageSize: 50,
      search: debouncedAccountQuery.trim() || undefined,
    });
  const accounts = accountsData?.data?.items ?? [];

  // La cuenta elegida se pide aparte para tener sus marcas y su nombre aunque no esté
  // en la página que devolvió el buscador.
  const { data: accountDetail } = useGetClientAccountQuery(clientAccountId, {
    skip: !clientAccountId,
  });
  const selectedAccount = accountDetail?.data ?? null;

  // El tipo de cliente es del registro: si la cuenta ya lo tiene, manda el suyo y aquí
  // solo se enseña. Si no lo tiene, el que se elija aquí se apunta en la cuenta al emitir.
  const registryEngagement = selectedAccount?.engagement ?? null;
  // Si el valor lo puso el registro y luego se elige un cliente sin tipo, vuelve al de
  // por defecto: quedarse con el del cliente anterior lo arrastraría sin que nadie lo
  // haya elegido.
  const engagementFromRegistry = useRef(false);
  useEffect(() => {
    if (registryEngagement) {
      setClientEngagement(registryEngagement);
      engagementFromRegistry.current = true;
    } else if (engagementFromRegistry.current) {
      setClientEngagement("ALWAYS_ON");
      engagementFromRegistry.current = false;
    }
  }, [registryEngagement]);

  const { data: tagsData } = useGetContentTagsQuery();
  const tags = useMemo(() => tagsData?.data ?? [], [tagsData]);

  // Una línea solo lleva tags de su House (o del catálogo de BeApolo). Una versión
  // anterior puede traer tags retirados, y cambiar la unidad de negocio deja los de la
  // otra: se quitan aquí, porque el backend los rechazaría al guardar.
  useEffect(() => {
    if (tags.length === 0) return;
    setProjects((current) => {
      let changed = false;
      const next = current.map((project) => {
        const valid = keepLineTags(
          project.contentTags,
          tags,
          showHouses,
          project.house
        );
        if (valid.length === project.contentTags.length) return project;
        changed = true;
        return { ...project, contentTags: valid };
      });
      return changed ? next : current;
    });
  }, [tags, showHouses, projects]);

  const { data: usersData } = useGetUsersQuery();
  const users = useMemo(
    () => (usersData?.data ?? []).filter((user) => user.active !== false),
    [usersData]
  );

  const [searchProspects, prospectSearch] = useLazySearchCrmProspectsQuery();
  const [prospectQuery, setProspectQuery] = useState("");

  const [createProposal, { isLoading: isSaving }] =
    useCreateCommercialProposalMutation();

  /**
   * Rellena el formulario con la propuesta origen.
   *
   * Se copia TODO menos las fechas de entrada y entrega, que son de la versión nueva:
   * una V2 se entrega otro día, y arrastrar las de la V1 haría que naciese con la fecha
   * límite ya pasada. La fecha de activación sí se copia, porque la campaña que se
   * propone suele seguir siendo la misma.
   *
   * El nombre también se copia, y eso es lo que hace que el número salga como la
   * versión siguiente: la familia es unidad + año + cuenta + marca + nombre. Si el
   * usuario cambia el nombre, deja de ser una versión y pasa a ser otra propuesta, que
   * es el comportamiento correcto.
   */
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    if (!source || prefilled) return;
    setBusinessUnit(source.businessUnit);
    setClientKind(source.clientKind);
    setClientEngagement(source.clientEngagement);
    setClientAccountId(source.clientAccountId);
    setClientBrandId(source.clientBrandId ?? "");
    setName(source.name);
    // Los ids, no los nombres: son los que casan con el value de los desplegables.
    setResponsableComercial(source.responsableComercialUserId ?? "");
    setResponsablePropuesta(source.responsablePropuestaUserId ?? "");
    setEstimatedActivationDate(source.estimatedActivationDate);
    setDurationMonths(String(source.estimatedDurationMonths));
    setProjects(
      source.projects.map((project) => ({
        lineName: project.lineName,
        house: project.house ?? "",
        budgetType: project.budgetType,
        budget: project.budget !== null ? String(project.budget) : "",
        contentTags: project.contentTags,
        scenarios: project.scenarios.map((scenario) => ({
          label: scenario.label,
          amount: String(scenario.amount),
          isReference: scenario.isReference,
        })),
      }))
    );
    setPrefilled(true);
  }, [source, prefilled]);

  const clearClientSelection = () => {
    setClientAccountId("");
    setClientBrandId("");
    setNewProspect(null);
  };

  /**
   * Cambiar de unidad o de tipo invalida la selección de cliente: los números son de la
   * unidad y del tipo, y una cuenta de BeApolo en una propuesta de House Group es un
   * error que el backend rechaza.
   *
   * Se limpia en los handlers y NO en un efecto sobre [businessUnit, clientKind]. Con un
   * efecto, la precarga de una versión nueva —que fija unidad, tipo y cliente a la vez—
   * disparaba el efecto justo después y borraba el cliente recién puesto.
   */
  const changeBusinessUnit = (next: CommercialBusinessUnit) => {
    setBusinessUnit(next);
    clearClientSelection();
    // "Agencia" solo existe en BeApolo: al volver a House Group hay que soltarla.
    if (next === "HOUSE_GROUP" && clientKind === "AGENCIA") {
      setClientKind("CLIENTE");
    }
    // En BeApolo no hay houses, así que las que hubiera elegidas se limpian.
    if (next === "BEAPOLO") {
      setProjects((current) =>
        current.map((project) => ({ ...project, house: "" }))
      );
    }
  };

  // El buscador del CRM: con debounce y desde 3 caracteres, que es lo que el backend
  // exige. Un 429 significa esperar, no que haya fallado nada.
  useEffect(() => {
    if (!isProspect || prospectQuery.trim().length < 3) return;
    const handle = setTimeout(() => {
      searchProspects({ q: prospectQuery.trim(), businessUnit });
    }, 300);
    return () => clearTimeout(handle);
  }, [prospectQuery, isProspect, businessUnit, searchProspects]);

  const brands = selectedAccount?.brands ?? [];

  const rateLimited =
    prospectSearch.error !== undefined &&
    typeof prospectSearch.error === "object" &&
    "status" in prospectSearch.error &&
    (prospectSearch.error as { status?: number }).status === 429;

  const hasClient = isProspect
    ? Boolean(clientAccountId) || Boolean(newProspect?.name)
    : Boolean(clientAccountId);

  const projectsValid = projects.every((project) => {
    if (!project.lineName.trim()) return false;
    if (showHouses && !project.house) return false;
    if (project.budgetType === "ESCENARIOS") {
      return (
        project.scenarios.length > 0 &&
        project.scenarios.every((scenario) => scenario.label.trim() !== "") &&
        project.scenarios.filter((scenario) => scenario.isReference).length ===
          1
      );
    }
    return project.budget.trim() !== "";
  });

  // Una version nueva se vuelve a entregar: necesita su propia fecha. La de la anterior
  // sigue guardada en ella, y el backend rechaza repetirla.
  const sameDeadlineAsPrevious =
    Boolean(source) &&
    deliveryDeadline !== "" &&
    deliveryDeadline === source?.deliveryDeadline;

  const datesValid =
    entryDate !== "" &&
    deliveryDeadline !== "" &&
    estimatedActivationDate !== "" &&
    deliveryDeadline >= entryDate &&
    !sameDeadlineAsPrevious;

  /**
   * Lo que falta para poder guardar.
   *
   * Existe porque un boton deshabilitado sin explicacion es imposible de depurar desde
   * el otro lado de la pantalla: pasó con la precarga de "nueva version", que dejaba los
   * dos responsables vacios y el boton muerto sin decir nada.
   */
  const missing: string[] = [
    !hasClient && t("commercialProposals.client", "Cliente"),
    name.trim() === "" &&
      t("commercialProposals.name", "Nombre de la propuesta"),
    responsableComercialUserId === "" &&
      t("commercialProposals.responsableComercial", "Responsable comercial"),
    responsablePropuestaUserId === "" &&
      t("commercialProposals.responsablePropuesta", "Responsable de propuesta"),
    !datesValid && t("commercialProposals.missing.dates", "Las tres fechas"),
    !(Number(estimatedDurationMonths) >= 1) &&
      t("commercialProposals.durationMonths", "Duración (meses)"),
    (projects.length === 0 || !projectsValid) &&
      t("commercialProposals.missing.projects", "Los proyectos, completos"),
  ].filter((entry): entry is string => Boolean(entry));

  const canSubmit = missing.length === 0 && !isSaving;

  const handleSubmit = async () => {
    const payload = {
      businessUnit,
      clientKind,
      clientEngagement,
      ...(clientAccountId
        ? { clientAccountId }
        : {
            prospect: {
              name: newProspect?.name ?? "",
              crmPursuitId: newProspect?.id,
            },
          }),
      ...(clientBrandId ? { clientBrandId } : {}),
      name: name.trim(),
      responsableComercialUserId,
      responsablePropuestaUserId,
      entryDate,
      deliveryDeadline,
      estimatedActivationDate,
      estimatedDurationMonths: Number(estimatedDurationMonths),
      projects: projects.map<ProposalProjectInput>((project) => ({
        lineName: project.lineName.trim(),
        ...(showHouses && project.house ? { house: project.house } : {}),
        budgetType: project.budgetType,
        ...(project.budgetType === "ESCENARIOS"
          ? {
              scenarios: project.scenarios.map((scenario) => ({
                label: scenario.label.trim(),
                amount: toNumber(scenario.amount),
                isReference: scenario.isReference,
              })),
            }
          : { budget: toNumber(project.budget) }),
        contentTags: project.contentTags,
      })),
    };

    try {
      const response = await createProposal(payload).unwrap();
      const data = response.data;
      const synced = data?.crmStatus === "SYNCED";
      dispatch(
        setToastMessage({
          message: synced
            ? t(
                "commercialProposals.new.created",
                "Propuesta {{number}} creada",
                { number: data?.proposalNumber }
              )
            : t(
                "commercialProposals.new.createdNoCrm",
                "Propuesta {{number}} guardada, pero el CRM no la ha confirmado. Se puede reintentar desde el listado.",
                { number: data?.proposalNumber }
              ),
          severity: synced ? "success" : "warning",
        })
      );
      navigate("/commercial-proposals");
    } catch (error: unknown) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ??
        t("commercialProposals.new.failed", "No se pudo crear la propuesta");
      dispatch(setToastMessage({ message, severity: "error" }));
    }
  };

  const section = (title: string, children: React.ReactNode) => (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${theme.palette.grey[200]}`,
        p: { xs: 2, md: 3 },
      }}
    >
      <Typography variant="subtitle1" mb={2}>
        {title}
      </Typography>
      {children}
    </Paper>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Box
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "64px" : "250px",
          width: { xs: "calc(100% - 64px)", sm: "auto" },
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <PageBreadcrumbs />

        <PageContainer
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <Box>
            <Typography variant="h5">
              {source
                ? t("commercialProposals.new.titleVersion", "Nueva versión")
                : t("commercialProposals.new.title", "Nueva propuesta")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(
                "commercialProposals.new.subtitle",
                "El número de propuesta se compone al guardar: unidad, año, cliente, marca, nombre y versión."
              )}
            </Typography>
          </Box>

          {loadingSource && (
            <Box textAlign="center" py={2}>
              <OrbLoader size="medium" />
            </Box>
          )}

          {source && (
            <Alert severity="info">
              {t(
                "commercialProposals.new.versionOf",
                'Vas a emitir la versión siguiente de "{{number}}". Si no cambias el nombre de la propuesta será la V{{next}}; si lo cambias, será una propuesta distinta con su propio V1. Las fechas de entrada y de entrega van en blanco: son de esta versión.',
                { number: source.proposalNumber, next: source.version + 1 }
              )}
            </Alert>
          )}

          {section(
            t("commercialProposals.new.clientSection", "Cliente"),
            <Stack gap={2}>
              <Stack direction={{ xs: "column", md: "row" }} gap={2}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>
                    {t("commercialProposals.businessUnit", "Unidad de negocio")}
                  </InputLabel>
                  <Select
                    value={businessUnit}
                    label={t(
                      "commercialProposals.businessUnit",
                      "Unidad de negocio"
                    )}
                    onChange={(event) =>
                      changeBusinessUnit(
                        event.target.value as CommercialBusinessUnit
                      )
                    }
                  >
                    <MenuItem value="HOUSE_GROUP">House Group</MenuItem>
                    <MenuItem value="BEAPOLO">BeApolo</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>
                    {t("commercialProposals.clientKind", "Cliente o prospect")}
                  </InputLabel>
                  <Select
                    value={clientKind}
                    label={t(
                      "commercialProposals.clientKind",
                      "Cliente o prospect"
                    )}
                    onChange={(event) => {
                      setClientKind(event.target.value as CommercialClientKind);
                      clearClientSelection();
                    }}
                  >
                    <MenuItem value="CLIENTE">
                      {t("commercialProposals.kind.CLIENTE", "Cliente")}
                    </MenuItem>
                    {/* Agencia solo en BeApolo, que es donde existe esa distinción. */}
                    {businessUnit === "BEAPOLO" && (
                      <MenuItem value="AGENCIA">
                        {t("commercialProposals.kind.AGENCIA", "Agencia")}
                      </MenuItem>
                    )}
                    <MenuItem value="PROSPECT">
                      {t("commercialProposals.kind.PROSPECT", "Prospect")}
                    </MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>
                    {t(
                      "commercialProposals.clientEngagement",
                      "Tipo de cliente"
                    )}
                  </InputLabel>
                  <Select
                    value={clientEngagement}
                    disabled={Boolean(registryEngagement)}
                    label={t(
                      "commercialProposals.clientEngagement",
                      "Tipo de cliente"
                    )}
                    onChange={(event) =>
                      setClientEngagement(
                        event.target.value as ClientEngagement
                      )
                    }
                  >
                    {CLIENT_ENGAGEMENTS.map((engagement) => (
                      <MenuItem key={engagement} value={engagement}>
                        {t(
                          `commercialProposals.engagement.${engagement}`,
                          engagement
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                  {registryEngagement && (
                    <FormHelperText>
                      {t(
                        "commercialProposals.engagementFromRegistry",
                        "Sale del registro de clientes"
                      )}
                    </FormHelperText>
                  )}
                </FormControl>
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} gap={2}>
                <Autocomplete
                  size="small"
                  sx={{ flex: 2 }}
                  options={accounts}
                  value={selectedAccount}
                  loading={loadingAccounts}
                  // El backend ya filtra por nombre y número; volver a filtrar aquí
                  // esconderÍa resultados que sí coinciden.
                  filterOptions={(options) => options}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  getOptionLabel={(option) =>
                    `${option.accountCode} — ${option.name}`
                  }
                  onInputChange={(_, value, reason) => {
                    if (reason === "input") setAccountQuery(value);
                  }}
                  onChange={(_, value) => {
                    setClientAccountId(
                      (value as ClientAccountView | null)?.id ?? ""
                    );
                    setClientBrandId("");
                    setNewProspect(null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        isProspect
                          ? t(
                              "commercialProposals.prospectRegistered",
                              "Prospect del registro"
                            )
                          : t("commercialProposals.client", "Cliente")
                      }
                      helperText={t(
                        "commercialProposals.clientHelp",
                        "Busca por nombre o por número de cliente."
                      )}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingAccounts && (
                              <OrbLoader size="inline" state="searching" />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                {brands.length > 0 && (
                  <FormControl size="small" sx={{ flex: 2 }}>
                    <InputLabel>
                      {t("commercialProposals.brand", "Marca (si aplica)")}
                    </InputLabel>
                    <Select
                      value={clientBrandId}
                      label={t(
                        "commercialProposals.brand",
                        "Marca (si aplica)"
                      )}
                      onChange={(event) => setClientBrandId(event.target.value)}
                    >
                      <MenuItem value="">
                        <em>{t("commercialProposals.noBrand", "Sin marca")}</em>
                      </MenuItem>
                      {brands.map((brand) => (
                        <MenuItem key={brand.id} value={brand.id}>
                          {brand.brandCode} — {brand.name} ({brand.countryCode})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Stack>

              {isProspect && !clientAccountId && (
                <Box>
                  <Autocomplete
                    size="small"
                    options={prospectSearch.data?.data ?? []}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.name
                    }
                    filterOptions={(options) => options}
                    loading={prospectSearch.isFetching}
                    value={newProspect}
                    onChange={(_, value) =>
                      setNewProspect(value as CrmProspectOption | null)
                    }
                    onInputChange={(_, value, reason) => {
                      setProspectQuery(value);
                      // Escribir a mano es válido: si el prospect no está en el CRM se da
                      // de alta con el nombre escrito, sin pursuit al que engancharlo.
                      if (reason === "input" && value.trim() !== "") {
                        setNewProspect({ id: "", name: value, stage: null });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t(
                          "commercialProposals.prospectSearch",
                          "Prospect nuevo (busca en el CRM o escribe el nombre)"
                        )}
                        helperText={
                          rateLimited
                            ? t(
                                "commercialProposals.prospectRateLimited",
                                "Demasiadas búsquedas seguidas. Espera unos segundos."
                              )
                            : t(
                                "commercialProposals.prospectHelp",
                                "Desde 3 letras. Si no aparece, escribe el nombre y se dará de alta con número nuevo."
                              )
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {prospectSearch.isFetching && (
                                <OrbLoader size="inline" state="searching" />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id || option.name}>
                        {option.name}
                        {option.stage && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            {option.stage}
                          </Typography>
                        )}
                      </li>
                    )}
                  />
                </Box>
              )}
            </Stack>
          )}

          {section(
            t("commercialProposals.new.proposalSection", "Propuesta"),
            <Stack gap={2}>
              <Stack direction={{ xs: "column", md: "row" }} gap={2}>
                <TextField
                  size="small"
                  label={t(
                    "commercialProposals.name",
                    "Nombre de la propuesta"
                  )}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  sx={{ flex: 2 }}
                  placeholder="Plan 3Q"
                />
                <FormControl size="small" sx={{ flex: 2 }}>
                  <InputLabel>
                    {t(
                      "commercialProposals.responsableComercial",
                      "Responsable comercial"
                    )}
                  </InputLabel>
                  <Select
                    value={responsableComercialUserId}
                    label={t(
                      "commercialProposals.responsableComercial",
                      "Responsable comercial"
                    )}
                    onChange={(event) =>
                      setResponsableComercial(event.target.value)
                    }
                  >
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {[user.firstName, user.lastName]
                          .filter(Boolean)
                          .join(" ") || user.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ flex: 2 }}>
                  <InputLabel>
                    {t(
                      "commercialProposals.responsablePropuesta",
                      "Responsable de propuesta"
                    )}
                  </InputLabel>
                  <Select
                    value={responsablePropuestaUserId}
                    label={t(
                      "commercialProposals.responsablePropuesta",
                      "Responsable de propuesta"
                    )}
                    onChange={(event) =>
                      setResponsablePropuesta(event.target.value)
                    }
                  >
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {[user.firstName, user.lastName]
                          .filter(Boolean)
                          .join(" ") || user.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} gap={2}>
                <TextField
                  size="small"
                  type="date"
                  label={t("commercialProposals.entryDate", "Fecha de entrada")}
                  value={entryDate}
                  onChange={(event) => setEntryDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  type="date"
                  label={t(
                    "commercialProposals.deliveryDeadline",
                    "Fecha límite de entrega"
                  )}
                  value={deliveryDeadline}
                  onChange={(event) => setDeliveryDeadline(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={
                    (deliveryDeadline !== "" && deliveryDeadline < entryDate) ||
                    sameDeadlineAsPrevious
                  }
                  helperText={
                    deliveryDeadline !== "" && deliveryDeadline < entryDate
                      ? t(
                          "commercialProposals.deliveryBeforeEntry",
                          "No puede ser anterior a la fecha de entrada"
                        )
                      : source
                        ? t(
                            "commercialProposals.newVersionDeadline",
                            "Fecha nueva: la versión anterior tenía el {{date}}",
                            { date: source.deliveryDeadline }
                          )
                        : ""
                  }
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  type="date"
                  label={t(
                    "commercialProposals.activationDate",
                    "Activación estimada"
                  )}
                  value={estimatedActivationDate}
                  onChange={(event) =>
                    setEstimatedActivationDate(event.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <NumberField
                  size="small"
                  label={t(
                    "commercialProposals.durationMonths",
                    "Duración (meses)"
                  )}
                  value={estimatedDurationMonths}
                  onChange={(event) => setDurationMonths(event.target.value)}
                  inputProps={{ min: 1 }}
                  sx={{ flex: 1 }}
                />
              </Stack>
            </Stack>
          )}

          {section(
            t("commercialProposals.new.projectsSection", "Proyectos"),
            <>
              {!showHouses && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t(
                    "commercialProposals.noHouses",
                    "En BeApolo los proyectos no llevan house."
                  )}
                </Alert>
              )}
              <ProjectsTable
                projects={projects}
                onChange={setProjects}
                showHouses={showHouses}
                tags={tags}
              />
            </>
          )}

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ pb: 4 }}
          >
            <Button onClick={() => navigate("/commercial-proposals")}>
              {t("commercialProposals.cancel", "Cancelar")}
            </Button>
            <Stack direction="row" alignItems="center" gap={2}>
              {missing.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {t("commercialProposals.missing.label", "Falta")}:{" "}
                  {missing.join(", ")}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {t(
                  "commercialProposals.projects.total",
                  "Presupuesto de propuesta"
                )}
                : <strong>{euro.format(proposalTotal(projects))}</strong>
              </Typography>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                {isSaving
                  ? t("commercialProposals.saving", "Guardando…")
                  : t("commercialProposals.save", "Crear propuesta")}
              </Button>
            </Stack>
          </Stack>
        </PageContainer>
      </Box>
    </Box>
  );
};

export default NewCommercialProposal;
